// src/pages/HelpCenter.js
import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
  InputAdornment,
  Alert,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  Flag as FlagIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  ArrowForward as ArrowIcon,
  Sms as SmsIcon,
  Cancel as CancelIcon,
  Security as SecurityIcon,
  Info as InfoIcon,
  TextFields as TextFieldsIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  MenuBook as MenuBookIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { userAPI } from '../services/api';

// Contact info constants
const PHONE_NUMBER = '1-855-730-1800';
const PHONE_NUMBER_RAW = '18557301800';
const EMAIL_ADDRESS = 'check@secondclear.com';

// ─── FAQ Data ──────────────────────────────────────────────────────────────────

const FAQ_DATA = [
  {
    category: 'Getting Started',
    questions: [
      {
        q: 'How do I use Second Clear?',
        a: 'When you receive a suspicious call, text, or email, just forward it to us and we\'ll analyze it for common fraud patterns.\n\nText/SMS: Forward the message or describe what happened to 1-855-730-1800\n\nVoice: Call 1-855-730-1800 and describe the suspicious call after the beep\n\nEmail: Forward the suspicious email to check@secondclear.com\n\nWe\'ll respond within seconds with an analysis of what we found.'
      },
      {
        q: 'How do I add or remove someone I\'m protecting?',
        a: '1. Go to People in your dashboard\n2. Click Add someone and enter their name and phone number\n3. To remove, click their name and select Remove\n\nTheir phone number is how we identify their messages \u2014 make sure it\'s the number they\'ll text or call from.'
      },
      {
        q: 'Does the person I\'m protecting need to do anything to set up?',
        a: 'No app downloads or accounts needed. They just text or call our number when something seems suspicious. We handle everything else.\n\nYou may want to:\n\u2022 Save our number in their phone as "Second Clear" or "Scam Check"\n\u2022 Practice with them once so they\'re comfortable'
      },
    ],
  },
  {
    category: 'Using the Service',
    questions: [
      {
        q: 'What do the risk levels mean?',
        a: 'High Concern: Multiple red flags detected. This has strong indicators of a scam. Proceed with extreme caution.\n\nMedium Concern: Some warning signs present. Worth verifying through official channels before taking action.\n\nLow Concern: No major red flags found. Doesn\'t mean it\'s guaranteed safe, but we didn\'t detect common scam patterns.\n\nEven if something scores "Low Concern," trust your gut. If it feels wrong, verify independently.'
      },
      {
        q: 'What kinds of scams can you detect?',
        a: 'We look for patterns common in:\n\u2022 IRS/government impersonation\n\u2022 Tech support scams\n\u2022 Grandparent/family emergency scams\n\u2022 Prize and lottery scams\n\u2022 Bank and financial scams\n\u2022 Package delivery scams\n\u2022 Medicare/Social Security scams\n\u2022 Romance and investment scams\n\nWe analyze the message content, not just keywords \u2014 so we can catch variations and new tactics.'
      },
      {
        q: 'Can I send screenshots?',
        a: 'Yes! If you receive a suspicious text, you can take a screenshot and send it as a picture message (MMS) to 1-855-730-1800. We\'ll read the text from the image and analyze it.\n\nThis is especially helpful if:\n\u2022 The original message won\'t forward properly\n\u2022 You want to show exactly what the screen looked like\n\u2022 The scam includes images or logos'
      },
      {
        q: 'My message didn\'t go through. What happened?',
        a: 'Phone carriers sometimes filter messages containing certain words before they reach us. This can happen with messages mentioning specific company names or urgent phrases.\n\nTry these alternatives:\n\u2022 Describe it instead: Text us "Someone called claiming to be from tech support and said my computer has a virus"\n\u2022 Send a screenshot: Take a picture of the message and send it as an MMS\n\u2022 Use email: Forward to check@secondclear.com \u2014 email doesn\'t have carrier filtering\n\nIf you\'re still having trouble, contact us at support@secondclear.com.'
      },
      {
        q: 'What if the message is really long?',
        a: 'No problem. We can handle long messages. If the response is long, it may arrive in multiple text messages \u2014 that\'s normal.'
      },
      {
        q: 'Can I ask follow-up questions?',
        a: 'Yes. After we analyze something, we may ask follow-up questions like:\n\u2022 Did you share any personal information?\n\u2022 Do you have the phone number they called from?\n\nJust reply naturally. If we ask for a phone number and you don\'t have it, just say "I don\'t have it" and we\'ll move on.'
      },
      {
        q: 'What if I get a follow-up question but want to submit something new?',
        a: 'Just send the new message. We\'ll recognize it as a new submission and analyze it fresh.'
      },
      {
        q: 'What about "wrong number" texts or friendly messages from strangers?',
        a: 'Some of the most dangerous scams start with innocent-looking messages. "Pig butchering" scams begin with friendly wrong-number texts like "Hey, are you coming to my barbecue?" or "Hi! Is this Amy?"\n\nWhy these scams are different: They\'re designed to be undetectable at first. There are no red flags in the opening message \u2014 that\'s intentional. The scammer\'s goal is to start a conversation, build a friendly relationship over days or weeks, then eventually steer you toward fake cryptocurrency or investment opportunities. By the time red flags appear, victims have already built trust with the scammer.\n\nThese scams have stolen over $75 billion worldwide. Individual victims often lose tens of thousands \u2014 sometimes their entire life savings.\n\nWhat to watch for:\n\u2022 A stranger who wants to keep chatting after you say "wrong number"\n\u2022 New online friends who bring up investing, crypto, or trading\n\u2022 Anyone who says they can help you make easy money\n\u2022 Investment platforms you\'ve never heard of\n\nOur advice: You\'re not obligated to respond to people you don\'t know. Even replying with a polite "wrong number" confirms your phone is active and that a real person is reading. It\'s okay to ignore and block without responding.\n\nIf someone you\'ve been chatting with starts talking about investments, send us that conversation \u2014 we can help you evaluate whether it\'s legitimate.\n\nNote: Because these scams are specifically designed to evade detection, we may not flag the initial "wrong number" message as suspicious. The opening text is intentionally innocent. Our best protection is education \u2014 now that you know this pattern exists, you can recognize it yourself.'
      },
    ],
  },
  {
    category: 'Privacy & Security',
    questions: [
      {
        q: 'Is my data private?',
        a: 'Yes. We take privacy seriously:\n\u2022 Your messages are encrypted in transit and at rest\n\u2022 We don\'t sell or share your data with third parties\n\u2022 We don\'t use your data for advertising\n\u2022 Only you and your caregiver (if applicable) can see your message history\n\nSee our full Privacy Policy for details.'
      },
      {
        q: 'I accidentally shared my Social Security number (or other personal info). What do I do?',
        a: 'First: Don\'t panic. Here\'s what to do:\n\n1. If you shared it with a scammer:\n\u2022 Freeze your credit at Equifax, Experian, and TransUnion\n\u2022 Report identity theft at IdentityTheft.gov\n\u2022 Monitor your accounts for unusual activity\n\n2. If you shared it with us: We automatically detect and redact sensitive information like Social Security numbers, credit card numbers, and passwords before storing your message. You\'ll see a notice if we detected and protected your information.'
      },
      {
        q: 'Who can see my messages?',
        a: '\u2022 If you have your own account: Only you\n\u2022 If you\'re protected by a caregiver: You and your caregiver\n\u2022 Our team: Only if needed to troubleshoot issues or improve the service\n\nWe never share your messages with anyone else.'
      },
      {
        q: 'Do you store my messages?',
        a: 'Yes, so you can review your history and so caregivers can monitor for patterns. Messages are stored securely and you can request deletion at any time.'
      },
    ],
  },
  {
    category: 'Troubleshooting',
    questions: [
      {
        q: 'I texted but didn\'t get a response',
        a: 'Check these common causes:\n\n1. Carrier filtering: Some messages get blocked before reaching us. Try describing the situation instead of forwarding, or use email.\n\n2. Wrong number: Make sure you\'re texting 1-855-730-1800 (all 11 digits)\n\n3. Unregistered phone: We only respond to registered phone numbers. Make sure the phone you\'re texting from is the one on your account.\n\n4. Service issue: Rarely, there may be a brief outage. Wait a few minutes and try again, or email check@secondclear.com instead.\n\nIf problems persist, contact support@secondclear.com.'
      },
      {
        q: 'The analysis seems wrong',
        a: 'We\'re not perfect. If you think we missed something or got it wrong:\n\n1. Use the feedback button on the message in your dashboard\n2. Tell us what we got wrong\n3. We\'ll use your feedback to improve\n\nScammers constantly evolve their tactics, so your feedback helps us stay current.'
      },
      {
        q: 'I\'m getting too many/too few notifications',
        a: 'Adjust your notification preferences in Settings > Notifications. You can choose to be notified:\n\u2022 For all messages\n\u2022 Only for high-risk messages\n\u2022 Daily summary only\n\u2022 Not at all'
      },
      {
        q: 'How do I report a scam phone number?',
        a: 'After we analyze a message, we\'ll ask if you have the phone number the scammer used. Just reply with the number and we\'ll:\n\u2022 Check it against known scam databases\n\u2022 Add it to our internal tracking\n\u2022 Help protect other users\n\nYou can also report numbers directly in the dashboard by clicking Report Number.'
      },
    ],
  },
  {
    category: 'Account & Billing',
    questions: [
      {
        q: 'How do I change my password?',
        a: '1. Go to Settings in your dashboard\n2. Click Change Password\n3. Enter your current password and your new password\n4. Click Save'
      },
      {
        q: 'How do I update my payment method?',
        a: '1. Go to Settings > Billing\n2. Click Manage Subscription\n3. This opens our secure payment portal where you can update your card'
      },
      {
        q: 'How do I cancel my subscription?',
        a: '1. Go to Settings > Billing\n2. Click Manage Subscription\n3. Select Cancel Subscription\n\nYou\'ll keep access until the end of your current billing period. We\'re sorry to see you go \u2014 if there\'s something we could do better, please let us know at support@secondclear.com.'
      },
      {
        q: 'What happens if my payment fails?',
        a: 'We\'ll retry a few times over the next week and send you an email notification. Update your payment method in Settings > Billing to avoid interruption.\n\nIf payment can\'t be processed after several attempts, your access will be paused until payment is resolved.'
      },
    ],
  },
  {
    category: 'Training',
    questions: [
      {
        q: 'What is training mode?',
        a: 'Training mode lets you send practice scam messages to help your loved ones recognize fraud patterns. It\'s like a fire drill for scams.\n\nThey receive what looks like a scam message, and we track whether they recognize it as suspicious.'
      },
      {
        q: 'How do I send a training message?',
        a: '1. Go to Training in your dashboard\n2. Select the person\n3. Choose a template or write your own\n4. Click Send Training\n\nWe\'ll let you know how they responded.'
      },
      {
        q: 'They failed a training. Now what?',
        a: 'That\'s exactly why training exists! Use it as a conversation starter:\n\u2022 Review the message together\n\u2022 Point out the red flags\n\u2022 Remind them it\'s always okay to check with you first\n\u2022 Try another training in a few weeks\n\nNo judgment \u2014 these scams are designed to fool people.'
      },
    ],
  },
];

// ─── Scam Types Data ───────────────────────────────────────────────────────────

const SCAM_TYPES = [
  {
    id: 'government',
    name: 'Government Impersonation',
    icon: '\uD83C\uDFDB\uFE0F',
    riskLevel: 'High',
    description: 'Scammers pretend to be from the IRS, Social Security, Medicare, or law enforcement to create fear and urgency.',
    howItWorks: [
      'You receive a call, text, or email claiming to be from a government agency',
      'They claim you owe money, your benefits are suspended, or there\'s a warrant for your arrest',
      'They demand immediate payment via gift cards, wire transfer, or cryptocurrency',
      'They threaten arrest, deportation, or loss of benefits if you don\'t pay immediately',
    ],
    exampleMessages: [
      'IRS URGENT: You owe $3,000 in back taxes. Pay immediately or face arrest. Call 800-555-1234.',
      'Social Security Administration: Your SSN has been suspended due to suspicious activity. Call immediately to avoid legal action.',
      'This is Officer Johnson from the Sheriff\'s Department. There\'s a warrant for your arrest. Pay $2,000 now to resolve this.',
    ],
    redFlags: [
      'Government agencies don\'t call demanding immediate payment',
      'They never ask for gift cards, wire transfers, or crypto',
      'They don\'t threaten arrest over the phone',
      'Real agencies send official letters first',
    ],
    whatToDo: 'Hang up. If you\'re concerned, call the agency directly using the number on their official website \u2014 never a number the caller gives you.',
  },
  {
    id: 'grandparent',
    name: 'Grandparent / Family Emergency',
    icon: '\uD83D\uDC74',
    riskLevel: 'High',
    description: 'Scammers pretend to be a grandchild or family member in trouble, exploiting your love and concern.',
    howItWorks: [
      'You receive a call from someone claiming to be your grandchild (or their lawyer/friend)',
      'They say they\'re in jail, in an accident, or stranded somewhere',
      'They need money immediately for bail, medical bills, or travel',
      'They beg you not to tell other family members',
    ],
    exampleMessages: [
      'Grandma, it\'s me! I\'m in jail and I need $500 for bail. Please don\'t tell mom and dad!',
      'This is Officer Davis. Your grandson was in an accident and needs $2,000 for medical treatment. He asked us to call you.',
      'Hi, it\'s your favorite grandchild! I\'m stuck in Mexico and lost my wallet. Can you wire me money?',
    ],
    redFlags: [
      'They say "it\'s me" without using their name (waiting for you to say it)',
      'They demand secrecy \u2014 "don\'t tell mom and dad"',
      'The voice sounds different but they claim to have a cold or injury',
      'They need gift cards or wire transfer, not a normal payment method',
    ],
    whatToDo: 'Hang up and call your grandchild directly at a number you already have. If you can\'t reach them, call another family member to verify before sending any money.',
  },
  {
    id: 'techsupport',
    name: 'Tech Support Scam',
    icon: '\uD83D\uDCBB',
    riskLevel: 'High',
    description: 'Scammers claim your computer has a virus and offer to "fix" it while stealing your money or data.',
    howItWorks: [
      'You receive a call, pop-up, or email claiming your computer is infected',
      'They claim to be from Microsoft, Apple, or a security company',
      'They ask for remote access to your computer',
      'They "find problems" and charge hundreds of dollars to fix them \u2014 or steal your data',
    ],
    exampleMessages: [
      'MICROSOFT ALERT: Your computer has been infected with a virus. Call 1-888-555-0199 immediately.',
      'This is Windows Technical Support. We\'ve detected a serious security breach on your computer.',
      'Apple Security: Your iCloud has been compromised. Call immediately to protect your data.',
    ],
    redFlags: [
      'Microsoft, Apple, and other companies never make unsolicited calls about your computer',
      'Pop-ups with phone numbers are almost always scams',
      'Legitimate companies don\'t ask for remote access',
      'They pressure you to act immediately',
    ],
    whatToDo: 'Don\'t call the number. Don\'t give remote access. Close any pop-ups (force quit if needed). If you\'re concerned about your computer, contact a local technician you trust.',
  },
  {
    id: 'prize',
    name: 'Prize & Lottery Scam',
    icon: '\uD83C\uDFB0',
    riskLevel: 'High',
    description: 'Scammers tell you that you\'ve won a prize, but you need to pay fees to claim it.',
    howItWorks: [
      'You\'re told you\'ve won a lottery, sweepstakes, or prize',
      'To claim your winnings, you need to pay taxes, shipping, or processing fees upfront',
      'They may send a fake check that will bounce after you\'ve sent them money',
      'The prize doesn\'t exist',
    ],
    exampleMessages: [
      'CONGRATULATIONS! You\'ve won $50,000! Pay $199 processing fee to claim your prize.',
      'You\'ve been selected for a $1,000 Walmart gift card! Just pay $4.99 shipping.',
      'Publisher\'s Clearing House: You\'ve won $2.5 million! Purchase $500 in gift cards to cover the taxes.',
    ],
    redFlags: [
      'You can\'t win a contest you didn\'t enter',
      'Legitimate prizes never require upfront payment',
      'Gift cards are never a valid payment method for legitimate fees',
      'Foreign lotteries are illegal to play from the US',
    ],
    whatToDo: 'If you have to pay to receive a prize, it\'s a scam. Delete the message and don\'t respond.',
  },
  {
    id: 'pigbutchering',
    name: 'Pig Butchering / Wrong Number Scam',
    icon: '\uD83D\uDC37',
    riskLevel: 'High',
    description: 'A long-con scam that starts with innocent "wrong number" texts and ends with devastating financial losses through fake investments.',
    howItWorks: [
      'You receive a friendly text that appears to be a wrong number ("Hey, are you coming to my barbecue?")',
      'You politely reply "wrong number" and they apologize but keep chatting',
      'Over days or weeks, they build a friendly (or romantic) relationship with you',
      'Eventually they mention how much money they\'re making with cryptocurrency or trading',
      'They invite you to invest on a platform they recommend \u2014 which is fake',
      'You see amazing "returns" on the fake platform, encouraging you to invest more',
      'When you try to withdraw, they ask for more money for "taxes" or "fees"',
      'Eventually they disappear with everything you invested',
    ],
    exampleMessages: [
      'Hey, are you coming to my barbecue on Saturday?',
      'Hi! Is this Amy? I think I have the wrong number, sorry!',
      'Long time no see! How have you been?',
      'Sorry wrong person! But since we\'re here, want to be friends?',
    ],
    redFlags: [
      'A stranger wants to keep chatting after a "wrong number"',
      'They\'re very friendly very quickly',
      'They eventually bring up investing, crypto, or trading',
      'They recommend a trading platform you\'ve never heard of',
      'The investment shows incredible returns',
      'You can\'t withdraw without paying additional fees',
    ],
    whatToDo: 'Don\'t respond to texts from people you don\'t know. Even replying with a polite "wrong number" confirms your phone is active and that a real person is reading. If someone you met online starts talking about investments, stop all contact. Never invest on a platform recommended by someone you only know online.',
    additionalInfo: 'Why we may not catch this: These scams are specifically designed to be undetectable. The opening message has no scam signals \u2014 it\'s intentionally innocent. The actual fraud happens weeks later. Our best protection is education: knowing these scams exist so you can recognize the pattern yourself.',
  },
  {
    id: 'banking',
    name: 'Bank & Financial Scam',
    icon: '\uD83C\uDFE6',
    riskLevel: 'High',
    description: 'Scammers impersonate your bank to steal account credentials or money.',
    howItWorks: [
      'You receive a call, text, or email appearing to be from your bank',
      'They claim there\'s suspicious activity on your account',
      'They ask you to verify your account by providing personal information or login credentials',
      'Or they ask you to move money to a "safe account" \u2014 which they control',
    ],
    exampleMessages: [
      'Chase Bank: Unusual activity detected on your account. Click here to verify your identity or your account will be locked.',
      'Did you authorize a $1,200 wire transfer? Reply YES or NO.',
      'Your Venmo payment of $847.00 is pending. If you did not authorize this, call 855-555-0199.',
    ],
    redFlags: [
      'Banks don\'t ask for your full password or PIN by phone/text/email',
      'Suspicious links go to strange URLs, not the bank\'s actual website',
      'They create urgency: "Your account will be locked"',
      'They ask you to transfer money for "safety"',
    ],
    whatToDo: 'Don\'t click links in texts or emails. Call your bank using the number on the back of your card or on your statement \u2014 never a number from the message.',
  },
  {
    id: 'delivery',
    name: 'Package Delivery Scam',
    icon: '\uD83D\uDCE6',
    riskLevel: 'Medium',
    description: 'Scammers send fake delivery notifications to steal personal information or small payments.',
    howItWorks: [
      'You receive a text or email saying a package couldn\'t be delivered',
      'You\'re asked to click a link to reschedule or pay a small fee',
      'The link leads to a fake site that steals your information',
      'Or you\'re charged a small fee that gives scammers your card number',
    ],
    exampleMessages: [
      'USPS: Your package cannot be delivered. Confirm your address and pay $2.95 redelivery fee.',
      'FedEx: Delivery attempted. Schedule redelivery and pay $3.50 customs fee.',
      'Your Amazon order is held at customs. Pay $4.99 import fee to release.',
    ],
    redFlags: [
      'You weren\'t expecting a package',
      'Real shipping companies don\'t charge fees via text',
      'The link is a strange URL, not the actual shipping company',
      'The fee is small \u2014 designed to seem harmless',
    ],
    whatToDo: 'Don\'t click links. If you\'re expecting a package, go directly to the shipping company\'s website or app and check your tracking number there.',
  },
  {
    id: 'medicare',
    name: 'Medicare & Health Insurance Scam',
    icon: '\uD83C\uDFE5',
    riskLevel: 'High',
    description: 'Scammers impersonate Medicare to steal personal information or money.',
    howItWorks: [
      'You receive a call claiming to be from Medicare',
      'They say they need to verify your information, issue a new card, or offer free services',
      'They ask for your Medicare number, Social Security number, or bank information',
      'They use this information for identity theft or medical fraud',
    ],
    exampleMessages: [
      'This is Medicare calling. We need to verify your information to send you your new card.',
      'You qualify for a free back brace covered by Medicare. Just verify your Medicare number.',
      'Medicare is offering a refund. Provide your bank account to receive your deposit.',
    ],
    redFlags: [
      'Medicare doesn\'t call to ask for your Medicare number \u2014 they already have it',
      'Medicare doesn\'t offer unsolicited free equipment',
      'Medicare doesn\'t call to verify information or offer refunds',
      'Medicare communications come by mail, not unsolicited calls',
    ],
    whatToDo: 'Hang up. Call 1-800-MEDICARE (1-800-633-4227) directly if you have questions about your coverage. Never give your Medicare number to someone who calls you.',
  },
  {
    id: 'romance',
    name: 'Romance Scam',
    icon: '\uD83D\uDC95',
    riskLevel: 'High',
    description: 'Scammers create fake profiles on dating sites to build relationships and eventually ask for money.',
    howItWorks: [
      'You meet someone online who seems perfect \u2014 attractive, successful, interested in you',
      'They quickly become emotionally invested, saying they love you',
      'They can never meet in person \u2014 they\'re abroad, in the military, on an oil rig, etc.',
      'Eventually, they have an emergency and need money',
      'The requests continue and escalate',
    ],
    exampleMessages: [
      'I\'ve never felt this way about anyone before. I can\'t wait to meet you in person.',
      'I\'m stuck overseas and my wallet was stolen. Can you wire me money for a flight home?',
      'I want to visit you but I need help with the travel costs. I\'ll pay you back.',
    ],
    redFlags: [
      'They fell in love very quickly (love bombing)',
      'They can\'t video chat or always have excuses',
      'They\'re always facing emergencies that require money',
      'They want to move off the dating site to email or WhatsApp',
      'Their photos may be stolen (try reverse image search)',
    ],
    whatToDo: 'Never send money to someone you haven\'t met in person. If they can\'t video chat, be very suspicious. Try reverse image searching their photos. Talk to friends or family about the relationship.',
  },
];

// ─── Tab Panel helper ──────────────────────────────────────────────────────────

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ─── Resources Section ──────────────────────────────────────────────────────

const QUICKGUIDE_IMAGE_URL = process.env.REACT_APP_QUICKGUIDE_IMAGE_URL || null;

function ResourcesSection() {
  const [sendingAll, setSendingAll] = useState(false);
  const [guideResult, setGuideResult] = useState(null);

  const handleSendToAll = useCallback(async () => {
    setSendingAll(true);
    setGuideResult(null);
    try {
      const res = await userAPI.getClients();
      const people = (res.data.clients || []).filter(s => s.phone);

      if (people.length === 0) {
        setGuideResult({ type: 'warning', message: 'No people with phone numbers found.' });
        return;
      }

      let sent = 0;
      let failed = 0;
      for (const person of people) {
        try {
          await userAPI.sendGuide(person.id);
          sent++;
        } catch {
          failed++;
        }
      }

      if (failed === 0) {
        setGuideResult({ type: 'success', message: `Guide sent to ${sent} ${sent !== 1 ? 'people' : 'person'}.` });
      } else {
        setGuideResult({ type: 'warning', message: `Guide sent to ${sent} ${sent !== 1 ? 'people' : 'person'}. ${failed} failed.` });
      }
    } catch (err) {
      console.error('Send to all error:', err);
      setGuideResult({ type: 'error', message: 'Failed to send guides. Please try again.' });
    } finally {
      setSendingAll(false);
    }
  }, []);

  return (
    <Paper sx={{ p: 4, mb: 4 }}>
      <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
        <MenuBookIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="h5">Resources</Typography>
      </Box>

      <Typography variant="body1" paragraph sx={{ textAlign: 'center' }}>
        Help your loved ones get started with a simple visual guide that shows them how to forward texts, call, and forward emails.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 3, height: '100%', textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>Quick Start Guide</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              A simple, visual reference card to keep handy. Shows the three ways to check something suspicious: text, call, and email.
            </Typography>

            {QUICKGUIDE_IMAGE_URL ? (
              <Box
                component="img"
                src={QUICKGUIDE_IMAGE_URL}
                alt="Quick Start Guide preview"
                sx={{
                  width: '100%',
                  maxWidth: 300,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  mb: 2,
                  display: 'block',
                  mx: 'auto',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 300,
                  height: 200,
                  borderRadius: 1,
                  border: '2px dashed',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  mx: 'auto',
                  bgcolor: 'grey.50',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Guide image preview
                </Typography>
              </Box>
            )}

            <Typography variant="body2" color="text.secondary">
              They can also text <strong>GUIDE</strong> to {PHONE_NUMBER} to receive this anytime.
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>Send Guide</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Send the quick start guide as a picture message (MMS) to everyone at once.
            </Typography>

            <Box sx={{ mt: 'auto' }}>
              {guideResult && (
                <Alert severity={guideResult.type} sx={{ mb: 2 }} onClose={() => setGuideResult(null)}>
                  {guideResult.message}
                </Alert>
              )}
              <Button
                variant="contained"
                startIcon={sendingAll ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                onClick={handleSendToAll}
                disabled={sendingAll}
                fullWidth
              >
                {sendingAll ? 'Sending...' : 'Send to Everyone'}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
}

// ─── How It Works Tab Component ────────────────────────────────────────────────

function HowItWorksTab() {
  return (
    <Box>
      {/* Quick Start Section */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Getting Started
        </Typography>

        <Grid container spacing={4} sx={{ mt: 2 }}>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  fontSize: 24,
                  fontWeight: 'bold'
                }}
              >
                1
              </Box>
              <Typography variant="h6" gutterBottom>Submit</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ minHeight: { md: 48 } }}>
                Forward a suspicious call, text, or email to us for review.
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  fontSize: 24,
                  fontWeight: 'bold'
                }}
              >
                2
              </Box>
              <Typography variant="h6" gutterBottom>Analyze</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ minHeight: { md: 48 } }}>
                We identify common fraud patterns and provide objective analysis.
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  fontSize: 24,
                  fontWeight: 'bold'
                }}
              >
                3
              </Box>
              <Typography variant="h6" gutterBottom>Decide</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ minHeight: { md: 48 } }}>
                Review your summary and decide what, if anything, to do next.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* How to Submit */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          How to Submit for Review
        </Typography>

        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={4}>
            <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
              <Box display="flex" alignItems="center" mb={2}>
                <EmailIcon color="primary" sx={{ mr: 1, fontSize: 28 }} />
                <Typography variant="h6">Email</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Forward suspicious emails directly to:
              </Typography>
              <Link
                href={`mailto:${EMAIL_ADDRESS}`}
                underline="hover"
                sx={{ display: 'block', mb: 2 }}
              >
                <Typography variant="h6" color="primary.main" fontWeight={600}>
                  {EMAIL_ADDRESS}
                </Typography>
              </Link>
              <Typography variant="body2" color="text.secondary">
                We'll analyze the content and sender patterns.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
              <Box display="flex" alignItems="center" mb={2}>
                <PhoneIcon color="primary" sx={{ mr: 1, fontSize: 28 }} />
                <Typography variant="h6">Phone Calls</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Call to report suspicious calls:
              </Typography>
              <Link
                href={`tel:+${PHONE_NUMBER_RAW}`}
                underline="hover"
                sx={{ display: 'block', mb: 1 }}
              >
                <Typography variant="h6" color="primary.main" fontWeight={600}>
                  {PHONE_NUMBER}
                </Typography>
              </Link>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Dial all 11 digits including the 1 at the beginning
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Describe what the caller said and we'll help you understand common patterns.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
              <Box display="flex" alignItems="center" mb={2}>
                <SmsIcon color="primary" sx={{ mr: 1, fontSize: 28 }} />
                <Typography variant="h6">Text Messages</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Text the suspicious message to:
              </Typography>
              <Link
                href={`sms:+${PHONE_NUMBER_RAW}`}
                underline="hover"
                sx={{ display: 'block', mb: 2 }}
              >
                <Typography variant="h6" color="primary.main" fontWeight={600}>
                  {PHONE_NUMBER}
                </Typography>
              </Link>
              <Typography variant="body2" color="text.secondary">
                You can copy/paste text or describe what you received.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* What We Do / Don't Do */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom>
              What Second Clear Does
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                <ListItemText
                  primary="Analyzes for common fraud patterns"
                  secondary="We identify known fraud patterns based on language, requests, and context."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                <ListItemText
                  primary="Delivers calm, neutral summaries"
                  secondary="No alarmist language, no scare tactics — just clear information."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                <ListItemText
                  primary="Gives you a second opinion"
                  secondary="Think of us as a knowledgeable friend you can consult."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                <ListItemText
                  primary="Respects your autonomy"
                  secondary="You always make the final decision on what to do."
                />
              </ListItem>
            </List>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              What Second Clear Does Not Do
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon><CancelIcon color="disabled" /></ListItemIcon>
                <ListItemText
                  primary="Block or filter calls/messages"
                  secondary="We don't intercept or prevent any communications."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CancelIcon color="disabled" /></ListItemIcon>
                <ListItemText
                  primary="Make decisions for you"
                  secondary="We provide information — you decide what's right for you."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CancelIcon color="disabled" /></ListItemIcon>
                <ListItemText
                  primary="Create panic or urgency"
                  secondary="Scammers use urgency. We don't."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CancelIcon color="disabled" /></ListItemIcon>
                <ListItemText
                  primary="Judge or lecture"
                  secondary="Everyone encounters questionable situations. That's why we're here."
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
      </Paper>

      {/* Understanding Summaries */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Understanding Your Summaries
        </Typography>

        <Typography variant="body1" paragraph>
          When you submit something for review, you'll receive a summary that includes:
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight={500} gutterBottom>
              Risk Indicators
            </Typography>
            <Box display="flex" gap={1} mb={2}>
              <Chip label="High Risk" color="error" size="small" />
              <Chip label="Low Risk" color="success" size="small" />
            </Box>
            <Typography variant="body2" color="text.secondary">
              These indicate the presence of known fraud patterns. A "high risk" label means we found multiple warning signs — not that you've definitely been targeted by a scammer.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight={500} gutterBottom>
              Detected Patterns
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We list specific patterns found, such as urgency language, requests for payment, impersonation of organizations, or suspicious links. Each pattern is explained so you understand why it was flagged.
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Text Commands Section */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <TextFieldsIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h5">Text Message Commands</Typography>
        </Box>

        <Typography variant="body1" paragraph>
          When texting our service number ({PHONE_NUMBER}), you can use these special commands:
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Command</strong></TableCell>
                <TableCell><strong>What It Does</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Chip label="HELP" size="small" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }} />
                </TableCell>
                <TableCell>
                  Get a quick reminder of how to use the service and contact information
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Chip label="STOP" size="small" color="error" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }} />
                </TableCell>
                <TableCell>
                  Unsubscribe from all text messages from Second Clear
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Chip label="START" size="small" color="success" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }} />
                </TableCell>
                <TableCell>
                  Re-subscribe to text messages after previously stopping
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Chip label="UNSTOP" size="small" color="success" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }} />
                </TableCell>
                <TableCell>
                  Same as START — re-subscribe to text messages
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Chip label="GUIDE" size="small" color="info" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }} />
                </TableCell>
                <TableCell>
                  Receive a visual Quick Start Guide showing how to use the service
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 3, p: 2, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.main' }}>
          <Typography variant="body2">
            <strong>Note:</strong> Any other text you send will be analyzed as a submission for review.
            You don't need to use any special format — just forward or type the suspicious message.
          </Typography>
        </Box>
      </Paper>

      {/* Resources */}
      <ResourcesSection />

      {/* Privacy & Data */}
      <Paper sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <SecurityIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h5">Privacy & Data Handling</Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" paragraph>
          Second Clear is designed with your privacy in mind:
        </Typography>

        <List dense>
          <ListItem>
            <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Submissions are processed for pattern analysis only" />
          </ListItem>
          <ListItem>
            <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Your data is never sold or shared with advertisers" />
          </ListItem>
          <ListItem>
            <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="You can delete your data at any time from Settings" />
          </ListItem>
          <ListItem>
            <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="We use industry-standard encryption for all data" />
          </ListItem>
          <ListItem>
            <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Sensitive information (like phone numbers in submitted texts) is automatically redacted" />
          </ListItem>
        </List>

        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            For complete details, see our{' '}
            <Link href="https://www.secondclear.com/privacy-policy" target="_blank" rel="noopener">
              Privacy Policy
            </Link>
            {' '}and{' '}
            <Link href="https://www.secondclear.com/terms-of-service" target="_blank" rel="noopener">
              Terms of Service
            </Link>.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}

// ─── FAQ Tab Component ─────────────────────────────────────────────────────────

function FAQTab() {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedPanel, setExpandedPanel] = useState(null);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : null);
  };

  const filteredData = useMemo(() => {
    if (!searchTerm) return FAQ_DATA;
    const lower = searchTerm.toLowerCase();
    return FAQ_DATA
      .map((category) => ({
        ...category,
        questions: category.questions.filter(
          (item) =>
            item.q.toLowerCase().includes(lower) ||
            item.a.toLowerCase().includes(lower)
        ),
      }))
      .filter((category) => category.questions.length > 0);
  }, [searchTerm]);

  return (
    <Box>
      <TextField
        fullWidth
        placeholder="Search help articles..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        size="small"
        sx={{ mb: 4 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
      />

      {filteredData.length === 0 && (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No results found for "{searchTerm}"
        </Typography>
      )}

      {filteredData.map((category) => (
        <Box key={category.category} sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {category.category}
          </Typography>
          {category.questions.map((item, idx) => {
            const panelKey = `${category.category}-${idx}`;
            return (
              <Accordion
                key={idx}
                disableGutters
                expanded={expandedPanel === panelKey}
                onChange={handleAccordionChange(panelKey)}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={500}>{item.q}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: 'pre-line' }}
                  >
                    {item.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      ))}

      <Paper
        sx={{
          mt: 6,
          p: 4,
          textAlign: 'center',
          bgcolor: 'primary.50',
          border: '1px solid',
          borderColor: 'primary.100',
        }}
      >
        <Typography variant="h6" gutterBottom>
          Still need help?
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Contact us at support@secondclear.com and we'll get back to you within
          24 hours.
        </Typography>
        <Button
          variant="contained"
          href="mailto:support@secondclear.com"
          startIcon={<EmailIcon />}
        >
          Contact Support
        </Button>
      </Paper>
    </Box>
  );
}

// ─── Scam Types Tab Component ──────────────────────────────────────────────────

function ScamTypesTab() {
  const [selectedScam, setSelectedScam] = useState(null);

  return (
    <Box>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Learn about common scam types so you can recognize them before they cause
        harm. Click any card to learn more.
      </Typography>

      <Grid container spacing={2}>
        {SCAM_TYPES.map((scam) => (
          <Grid item xs={12} sm={6} md={4} key={scam.id}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                '&:hover': { boxShadow: 3 },
                transition: 'box-shadow 0.2s',
              }}
            >
              <CardActionArea
                onClick={() => setSelectedScam(scam)}
                sx={{ height: '100%', p: 2 }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                    <Typography variant="h5" component="span">
                      {scam.icon}
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {scam.name}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 1.5,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {scam.description}
                  </Typography>
                  <Chip
                    label={`${scam.riskLevel} Risk`}
                    size="small"
                    color={scam.riskLevel === 'High' ? 'error' : 'warning'}
                  />
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Detail Dialog */}
      <Dialog
        open={Boolean(selectedScam)}
        onClose={() => setSelectedScam(null)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        {selectedScam && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 6 }}>
              <Typography variant="h5" component="span">
                {selectedScam.icon}
              </Typography>
              <Box>
                <Typography variant="h6">{selectedScam.name}</Typography>
                <Chip
                  label={`${selectedScam.riskLevel} Risk`}
                  size="small"
                  color={selectedScam.riskLevel === 'High' ? 'error' : 'warning'}
                />
              </Box>
              <IconButton
                onClick={() => setSelectedScam(null)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Typography paragraph>{selectedScam.description}</Typography>

              {/* How it works */}
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                How it works:
              </Typography>
              <List dense disablePadding sx={{ mb: 3 }}>
                {selectedScam.howItWorks.map((step, i) => (
                  <ListItem key={i} sx={{ pl: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {i + 1}.
                      </Typography>
                    </ListItemIcon>
                    <ListItemText
                      primary={step}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>

              {/* Example messages */}
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Example messages:
              </Typography>
              <Box sx={{ mb: 3 }}>
                {selectedScam.exampleMessages.map((msg, i) => (
                  <Paper
                    key={i}
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 1,
                      bgcolor: 'grey.50',
                      fontStyle: 'italic',
                    }}
                  >
                    <Typography variant="body2">"{msg}"</Typography>
                  </Paper>
                ))}
              </Box>

              {/* Red flags */}
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Red flags:
              </Typography>
              <List dense disablePadding sx={{ mb: 3 }}>
                {selectedScam.redFlags.map((flag, i) => (
                  <ListItem key={i} sx={{ pl: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <FlagIcon color="error" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={flag}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>

              {/* What to do */}
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  What to do:
                </Typography>
                <Typography variant="body2">
                  {selectedScam.whatToDo}
                </Typography>
              </Alert>

              {/* Additional info (pig butchering) */}
              {selectedScam.additionalInfo && (
                <Alert severity="warning">
                  <Typography variant="body2">
                    {selectedScam.additionalInfo}
                  </Typography>
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedScam(null)} variant="contained">
                Got it
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}

// ─── Contact Support Tab Component ─────────────────────────────────────────────

function ContactSupportTab() {
  return (
    <Box>
      <Paper sx={{ p: 4, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Get in Touch
        </Typography>
        <Typography color="text.secondary" paragraph>
          We're here to help. Reach out and we'll get back to you within 24
          hours, usually much faster.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <EmailIcon color="primary" />
                <Typography variant="subtitle1" fontWeight={600}>
                  Email Support
                </Typography>
              </Box>
              <Link href="mailto:support@secondclear.com" underline="hover">
                <Typography variant="h6" color="primary.main">
                  support@secondclear.com
                </Typography>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Best for account issues, billing questions, and general help.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <PhoneIcon color="primary" />
                <Typography variant="subtitle1" fontWeight={600}>
                  Check a Message
                </Typography>
              </Box>
              <Link href="tel:+18557301800" underline="hover">
                <Typography variant="h6" color="primary.main">
                  1-855-730-1800
                </Typography>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Text or call to submit a suspicious message for analysis.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 4 }}>
        <Typography variant="h6" gutterBottom>
          When contacting support, please include:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon>
              <ArrowIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText primary="The email address on your account" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <ArrowIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText primary="A description of the issue" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <ArrowIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText primary="Screenshots if relevant" />
          </ListItem>
        </List>
      </Paper>
    </Box>
  );
}

// ─── Main Help Center Component ────────────────────────────────────────────────

const HelpCenter = () => {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Help Center
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Find answers, learn about scam types, and get support.
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, newValue) => setTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{ style: { display: 'none' } }}
          sx={{
            '& .MuiTab-root': {
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              mx: 0.5,
              minHeight: 48,
              textTransform: 'none',
              fontWeight: 500,
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'white',
                borderColor: 'primary.main',
              },
            },
          }}
        >
          <Tab label="How It Works" />
          <Tab label="FAQ" />
          <Tab label="Common Scam Types" />
          <Tab label="Contact Support" />
        </Tabs>
      </Paper>

      <TabPanel value={tab} index={0}>
        <HowItWorksTab />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <FAQTab />
      </TabPanel>
      <TabPanel value={tab} index={2}>
        <ScamTypesTab />
      </TabPanel>
      <TabPanel value={tab} index={3}>
        <ContactSupportTab />
      </TabPanel>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
        <Button
          startIcon={<KeyboardArrowUpIcon />}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          Back to Top
        </Button>
      </Box>
    </Box>
  );
};

export default HelpCenter;
